import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  MagnifyingGlass, CheckCircle, XCircle, ArrowLeft, DownloadSimple,
  Leaf, Calendar, MapPin, Hash, User as UserIcon, Certificate,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("serial") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const runVerify = async (q) => {
    const id = (q ?? query).trim();
    if (!id) {
      setError("Please enter a serial number or certificate ID.");
      return;
    }
    setError(""); setResult(null); setLoading(true);
    try {
      const { data } = await axios.get(`${API}/registry/verify/${encodeURIComponent(id)}`);
      setResult(data);
      setParams({ serial: id });
    } catch (e) {
      setError(e.response?.data?.detail || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-run if ?serial= present on first load
  useEffect(() => {
    const s = params.get("serial");
    if (s) runVerify(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="verify-page">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-4">
          <Link to="/" className="text-stone-500 hover:text-stone-900" data-testid="verify-back-home">
            <ArrowLeft size={20} />
          </Link>
          <Certificate size={22} weight="duotone" className="text-emerald-700" />
          <div className="flex-1">
            <h1 className="font-heading text-xl font-semibold text-stone-900">Carbon Credit Verification</h1>
            <p className="text-xs text-stone-500">Public registry — verify any Conexus Foundation carbon credit by serial number.</p>
          </div>
          <Link to="/carbon-hub" className="hidden sm:inline-block text-xs text-emerald-700 hover:text-emerald-900 font-medium">
            Carbon Hub →
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Search box */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-stone-200 rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-sm"
        >
          <label className="block text-xs uppercase tracking-wider text-stone-400 mb-3">
            Serial number or certificate ID
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runVerify()}
              placeholder="e.g. GS-REF-2026-000142"
              className="flex-1 px-4 py-3 border border-stone-200 rounded-xl text-sm font-mono focus:outline-none focus:border-emerald-500"
              data-testid="verify-input"
            />
            <button
              onClick={() => runVerify()}
              disabled={loading}
              className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium px-6 py-3 rounded-xl flex items-center justify-center gap-2 disabled:bg-stone-400"
              data-testid="verify-submit"
            >
              <MagnifyingGlass size={16} weight="bold" /> {loading ? "Searching..." : "Verify"}
            </button>
          </div>
          <p className="text-[11px] text-stone-400 mt-3">
            Tip: Serials follow the format <code>GS-PROJECT-YEAR-SERIAL</code>. You can also paste a certificate UUID.
          </p>
          {error && (
            <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="verify-error">
              {error}
            </p>
          )}
        </motion.div>

        {/* Result */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            {result.valid ? (
              <ValidCertificate result={result} />
            ) : (
              <InvalidResult message={result.message} />
            )}
          </motion.div>
        )}

        {/* How it works */}
        <div className="mt-12 text-center">
          <h3 className="font-heading text-sm font-semibold text-stone-900 mb-3">How verification works</h3>
          <div className="grid sm:grid-cols-3 gap-4 text-left">
            <Step n="1" title="Unique serial" desc="Every credit receives a tamper-proof serial generated at issuance." />
            <Step n="2" title="Public ledger" desc="Certificates are stored in our public registry, queryable here." />
            <Step n="3" title="Permanent retirement" desc="Once retired, credits cannot be re-sold or double-counted." />
          </div>
        </div>
      </main>
    </div>
  );
}

function ValidCertificate({ result }) {
  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}${result.download_url}`;
  return (
    <div className="bg-white border-2 border-emerald-200 rounded-2xl sm:rounded-3xl overflow-hidden" data-testid="verify-valid">
      <div className="bg-emerald-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
        <CheckCircle size={20} weight="fill" className="text-white shrink-0" />
        <span className="text-white text-xs sm:text-sm font-medium uppercase tracking-wider truncate">Valid · {result.status}</span>
        <span className="ml-auto text-[10px] text-emerald-100 uppercase tracking-wider whitespace-nowrap">{result.kind}</span>
      </div>
      <div className="p-4 sm:p-6 space-y-5">
        <div className="flex items-baseline flex-wrap gap-2">
          <span className="font-heading text-4xl sm:text-5xl font-light text-stone-900" data-testid="verify-tons">
            {Number(result.tons_co2e).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}
          </span>
          <span className="text-sm text-stone-500">tCO₂e retired</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <KV icon={<Hash size={14} />} label="Serial number" value={result.serial_number} mono />
          <KV icon={<Hash size={14} />} label="Certificate ID" value={result.certificate_id} mono small />
          <KV icon={<UserIcon size={14} />} label="Holder" value={result.holder_name} />
          <KV icon={<UserIcon size={14} />} label="Email (masked)" value={result.holder_email_masked || "—"} />
          <KV icon={<Calendar size={14} />} label="Issued" value={result.issue_date} />
          <KV icon={<Calendar size={14} />} label="Vintage year" value={String(result.vintage_year)} />
          <KV icon={<Calendar size={14} />} label="Period" value={`${result.period?.start || "—"} → ${result.period?.end || "—"}`} />
          <KV icon={<Leaf size={14} />} label="Project type" value={result.project?.type || "—"} />
          <KV icon={<MapPin size={14} />} label="Project" value={result.project?.name || "—"} />
          <KV icon={<MapPin size={14} />} label="Location" value={result.project?.location || "—"} />
        </div>

        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium px-5 py-3 rounded-xl"
          data-testid="verify-download-cert"
        >
          <DownloadSimple size={16} weight="bold" /> Download Certificate (PDF)
        </a>
      </div>
    </div>
  );
}

function InvalidResult({ message }) {
  return (
    <div className="bg-white border-2 border-rose-200 rounded-3xl p-8 text-center" data-testid="verify-invalid">
      <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-3">
        <XCircle size={28} weight="fill" className="text-rose-600" />
      </div>
      <h3 className="font-heading text-lg font-semibold text-stone-900 mb-1">Not found</h3>
      <p className="text-sm text-stone-500">{message}</p>
    </div>
  );
}

function KV({ icon, label, value, mono, small }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-stone-400 mb-0.5">
        {icon} {label}
      </div>
      <div className={`${mono ? "font-mono" : ""} ${small ? "text-xs" : "text-sm"} font-medium text-stone-900 break-all`}>
        {value}
      </div>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4">
      <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold flex items-center justify-center mb-2">
        {n}
      </div>
      <h4 className="text-sm font-semibold text-stone-900 mb-1">{title}</h4>
      <p className="text-xs text-stone-500">{desc}</p>
    </div>
  );
}
